import React from "react";
import { Container, Typography, Box } from "@mui/material";
import { motion } from "framer-motion";
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import ShowCaseCard from './ShowCaseCard';
import { useUIContext } from '../context/UIContext';
import incomeMetrics from '../assets/income-metrics.png';
import monthlyRevenue from '../assets/monthly-revenues.png';
import weeklyRevenue from '../assets/weekly-revenues.png';
import customersPage from '../assets/customers-page.png';
import customerNetworkUsage from '../assets/customer-network-usage.png';
import planSetup from '../assets/plan-setup.png';
import vouchersPage from '../assets/vouchers-page.png';
import routerManagement from '../assets/mikrotik-remote-management.png';

const DashboardShowcase = () => {
    const { viewport } = useUIContext();

    const getSlidesPerView = () => {
        if (viewport === 'mobile') return 1;
        if (viewport === 'tablet') return 2;
        return 3;
    };

    const slideData = [
        {
            image: incomeMetrics,
            altText: "View Your Analytics",
            title: "View Your Analytics",
            description: "Monitor your metrics in real-time for better decision-making.",
        },
        {
            image: monthlyRevenue,
            altText: "Track Your Monthly Revenue",
            title: "Track Your Monthly Revenue",
            description: "Visualize and analyze your monthly revenue trends.",
        },
        {
            image: weeklyRevenue,
            altText: "View Your Weekly Revenue",
            title: "View Your Weekly Revenue",
            description: "Gain insights into your daily and weekly earnings.",
        },
        {
            image: customersPage,
            altText: "Manage Customers",
            title: "Manage Customers",
            description: "Easily track and manage customer details and activity.",
        },
        {
            image: customerNetworkUsage,
            altText: "Visualize Customer Network Usage",
            title: "Visualize Customer Network Usage",
            description: "Track network usage for efficient resource management.",
        },
        {
            image: planSetup,
            altText: "Setup Internet Plans",
            title: "Setup Internet Plans",
            description: "Create and customize internet plans to meet customer needs.",
        },
        {
            image: vouchersPage,
            altText: "Manage Vouchers",
            title: "Manage Vouchers",
            description: "Generate and monitor vouchers for your customers.",
        },
        {
            image: routerManagement,
            altText: "Manage Router Management",
            title: "Manage Router Remotely",
            description: "Manage your routers remotely from one place, even when you don't have a public IP.",
        }
    ];

    return (
        <Box
            sx={{
                backgroundColor: '#f5f5f7',
                py: { xs: 10, md: 18 },
            }}
            id="dashboard"
        >
            <Container maxWidth="lg">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
                >
                    <Typography
                        variant="h2"
                        align="center"
                        sx={{
                            fontWeight: 700,
                            letterSpacing: '-0.03em',
                            background: 'linear-gradient(135deg, #19b594 0%, #0b74c1 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            mb: 2,
                        }}
                    >
                        See NasRadius in Action
                    </Typography>
                    <Typography
                        sx={{
                            textAlign: 'center',
                            color: 'rgba(29,29,31,0.65)',
                            fontSize: 'clamp(1.05rem, 0.3vw + 0.5rem, 1.25rem)',
                            maxWidth: '600px',
                            mx: 'auto',
                            mb: { xs: 6, md: 10 },
                            lineHeight: 1.6,
                        }}
                    >
                        Explore the full dashboard and see how NasRadius simplifies ISP management.
                    </Typography>
                </motion.div>

                <Swiper
                    spaceBetween={24}
                    slidesPerView={getSlidesPerView()}
                    loop={true}
                    pagination={{ clickable: true }}
                    autoplay={{
                        delay: 2500,
                        disableOnInteraction: false,
                    }}
                    modules={[Pagination, Autoplay]}
                    style={{
                        paddingBottom: '48px',
                    }}
                >
                    {slideData.map((slide, index) => (
                        <SwiperSlide key={index}>
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{
                                    duration: 0.5,
                                    delay: index * 0.05,
                                    ease: [0.25, 0.1, 0.25, 1],
                                }}
                            >
                                <ShowCaseCard
                                    image={slide.image}
                                    altText={slide.altText}
                                    title={slide.title}
                                    description={slide.description}
                                />
                            </motion.div>
                        </SwiperSlide>
                    ))}
                </Swiper>
            </Container>
        </Box>
    );
};

export default DashboardShowcase;
